export function parseAnalysisResult(resultText = '') {
  const cleanText = String(resultText).replace(/\*\*/g, '').trim();

  const escapeRegex = (value = '') =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const extractValue = (labels = []) => {
    for (const label of labels) {
      const escapedLabel = escapeRegex(label);

      const inlineRegex = new RegExp(`^\\s*${escapedLabel}:\\s*(.+)$`, 'im');
      const inlineMatch = cleanText.match(inlineRegex);
      if (inlineMatch) return inlineMatch[1].trim();

      const nextLineRegex = new RegExp(
        `^\\s*${escapedLabel}:\\s*$\\n\\s*(.+)$`,
        'im'
      );
      const nextLineMatch = cleanText.match(nextLineRegex);
      if (nextLineMatch) return nextLineMatch[1].trim();
    }

    return '';
  };

  const extractSection = (label = '', nextLabels = []) => {
    if (!cleanText || !label) return '';

    const escapedLabel = escapeRegex(label);
    const escapedNextLabels = nextLabels.map(escapeRegex);

    const endPattern =
      escapedNextLabels.length > 0
        ? `(?=\\n\\s*(?:${escapedNextLabels.join('|')})\\s*:|$)`
        : '$';

    const regex = new RegExp(
      `(?:^|\\n)\\s*${escapedLabel}\\s*:\\s*([\\s\\S]*?)${endPattern}`,
      'i'
    );

    const match = cleanText.match(regex);
    return match?.[1]?.trim() || '';
  };

  const parseBulletLines = (text = '') => {
    return String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^[-•*]\s*/, '').trim())
      .filter(Boolean);
  };

  const parseTechnicalLines = (text = '') => {
    return parseBulletLines(text).map((line) => {
      const [label, ...rest] = line.split(':');
      const value = rest.join(':').trim();

      return {
        label: value ? label.trim() : 'Context',
        value: value || line,
      };
    });
  };

  const newFormatLabels = [
    'Support',
    'Support Reference',
    'Support Zone',
    'Entry Zone',

    'Structural Change Level',
    'Invalidation Level',
    'Stop Loss',

    'Resistance',
    'Resistance Reference',
    'Resistance Level',
    'Resistance Target',
    'Target',

    'Current Structure',
    'Structure Notes',
    'Technical Context',
    'What to Watch',
    'AI Explanation',
    'Explanation',
  ];

  const extractNewSection = (label) =>
    extractSection(
      label,
      newFormatLabels.filter((item) => item !== label)
    );

  const supportReference =
    extractNewSection('Support') ||
    extractNewSection('Support Reference') ||
    extractNewSection('Support Zone') ||
    extractValue(['Support', 'Support Reference', 'Support Zone', 'Entry Zone']);

  const structuralChangeLevel =
    extractNewSection('Structural Change Level') ||
    extractNewSection('Invalidation Level') ||
    extractValue(['Structural Change Level', 'Invalidation Level', 'Stop Loss']);

  const resistanceReference =
    extractNewSection('Resistance') ||
    extractNewSection('Resistance Reference') ||
    extractNewSection('Resistance Level') ||
    extractValue([
      'Resistance',
      'Resistance Reference',
      'Resistance Level',
      'Resistance Target',
      'Target',
    ]);

  const currentStructure = extractNewSection('Current Structure');
  const structureNotesRaw = extractNewSection('Structure Notes');
  const technicalContextRaw = extractNewSection('Technical Context');
  const whatToWatchRaw = extractNewSection('What to Watch');

  const explanationMatch = cleanText.match(
    /(?:AI Explanation|Explanation):\s*([\s\S]*)/i
  );

  const legacyExplanation = explanationMatch ? explanationMatch[1].trim() : '';

  const legacySupportZone = extractValue(['Support Zone', 'Entry Zone']);
  const legacyInvalidationLevel = extractValue([
    'Invalidation Level',
    'Stop Loss',
  ]);
  const legacyResistanceLevel = extractValue([
    'Resistance Level',
    'Target',
    'Resistance Target',
  ]);

  const hasNewResearchFormat =
    !!supportReference ||
    !!structuralChangeLevel ||
    !!resistanceReference ||
    !!currentStructure ||
    !!structureNotesRaw ||
    !!technicalContextRaw ||
    !!whatToWatchRaw;

  return {
    // Legacy fields kept for backward compatibility.
    technicalConviction: extractValue(['Technical Conviction', 'Tier']),
    supportZone: legacySupportZone || supportReference,
    invalidationLevel: legacyInvalidationLevel || structuralChangeLevel,
    resistanceLevel: legacyResistanceLevel || resistanceReference,
    explanation: legacyExplanation,

    // Current research-snapshot fields.
    supportReference,
    structuralChangeLevel,
    resistanceReference,
    currentStructure,
    structureNotesRaw,
    structureNotes: parseBulletLines(structureNotesRaw),
    technicalContextRaw,
    technicalContext: parseTechnicalLines(technicalContextRaw),
    whatToWatchRaw,
    whatToWatch: parseBulletLines(whatToWatchRaw),
    hasNewResearchFormat,
  };
}